import React, { createContext, useContext, useState } from 'react';
import Cookies from 'js-cookie';

const DEFAULT_UNIT = 'sqft';

const UNIT_TO_M2 = {
    'sqm': 1,
    'sqft': 10.7639150512
};

/**
 * @type {import('$types/components/contexts').ConvertUnitOfAreaFn}
 */
const convertUnitOfArea = ({
    amount,
    from,
    to,
}) => {
    if (UNIT_TO_M2[from] === undefined || !UNIT_TO_M2[to] === undefined) {
        throw new Error('Unsupported unit.');
    }

    return (amount / UNIT_TO_M2[from]) * UNIT_TO_M2[to];
};

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

/** @type {import('$types/components/contexts').AreaUnitsContext} */
const AreaUnitsContext = createContext({
    convertUnitOfArea,
    setUnit: () => {},
    unit: DEFAULT_UNIT,
});

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const useAreaUnits = () => {
    const context = useContext(AreaUnitsContext);

    if (context === undefined) {
        throw new Error('useAreaUnits must be used within a AreaUnitsContext.');
    }

    return context;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/** @type {import('$types/components/contexts').AreaUnitsContextProvider} */
const AreaUnitsProvider = ({
    children,
    defaultUnit = DEFAULT_UNIT
}) => {

    /** @type {import('$types/components/contexts').AreaUnitsState} */
    const [unit, setUnit] = useState(() => {
        try {
            const firstPathSegment = (typeof window !== 'undefined'
                ? window.location.pathname.split('/').filter(Boolean)[0]
                : '') || '';
            const stored = Cookies.get(`${firstPathSegment}_area_unit_cookie`);
            if (stored) { return JSON.parse(stored); }
        } catch {}
        return defaultUnit;
    });

    return (
        <AreaUnitsContext.Provider value={{
            convertUnitOfArea,
            setUnit,
            unit,
        }}>
            {children}
        </AreaUnitsContext.Provider>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

AreaUnitsContext.displayName = 'AreaUnitsContext';

export {
    AreaUnitsProvider,
    useAreaUnits
};
